/**
 * Schedule Analysis — Yousef-only forensic delay + DCMA-14 workbench.
 * Overview auto-runs the full pipeline (events → windows → DCMA) and presents
 * an executive brief with charts; detail tabs keep the underlying tables.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import { isScheduleAnalysisAllowed } from '../../../config/proposalScheduleAccess';
import ScheduleAnalysisOverview, {
  ACCENT, CLASS_COLOR, CLASS_LABEL, PARTY_STYLE, ScoreRing, revLabel, windowParty, fmtIso,
} from './ScheduleAnalysisOverview';
import DataStandardTab from './DataStandardTab';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'events', label: 'Events' },
  { id: 'network', label: 'Network' },
  { id: 'windows', label: 'Windows' },
  { id: 'health', label: 'Health' },
  { id: 'standard', label: 'Data Standard' },
  { id: 'chat', label: 'Chat' },
];

const btn = {
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  color: '#334155',
};
const btnPrimary = { ...btn, background: ACCENT, borderColor: ACCENT, color: '#fff', fontWeight: 600 };

const CHAT_SUGGESTIONS = [
  'Why did the finish date move in the last window?',
  'Which delays on the driving path are not LGL\u2019s?',
  'Which DCMA checks are failing and why?',
  'What did the notebooks say about the biggest slipping activity?',
];

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function filenameFromHeader(headers, fallback) {
  const raw = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
  const star = raw.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1]); } catch { /* keep looking */ }
  }
  const quoted = raw.match(/filename="([^"]+)"/);
  return quoted ? quoted[1] : fallback;
}

function fmtMetricValue(m) {
  if (m.value == null) return '—';
  if (m.unit === 'ratio') return `${Math.round(m.value * 1000) / 10}%`;
  if (m.unit === 'index') return String(m.value);
  return String(m.value);
}

const ScheduleAnalysis = ({ jobNumber, jobName, projectId }) => {
  const { instance, accounts } = useMsal();
  const allowed = isScheduleAnalysisAllowed(accounts[0]?.username);
  const { viewers, isConnected } = usePresence('scheduleAnalysis', projectId || jobNumber || 'global', allowed);
  const chatEndRef = useRef(null);
  const autoRanRef = useRef(false);
  const networkFetchedRef = useRef(false);

  const [tab, setTab] = useState('overview');
  const [showHelp, setShowHelp] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);
  const [runStage, setRunStage] = useState(null);
  const [bootDone, setBootDone] = useState(false);

  const [revisions, setRevisions] = useState([]);
  const [rev, setRev] = useState('');
  const [events, setEvents] = useState([]);
  const [eventCounts, setEventCounts] = useState(null);
  const [eventFilter, setEventFilter] = useState('all');
  const [eventSearch, setEventSearch] = useState('');

  const [network, setNetwork] = useState(null);
  const [summary, setSummary] = useState(null);

  const [windows, setWindows] = useState(null);
  const [dcma, setDcma] = useState(null);
  const [dcmaOpen, setDcmaOpen] = useState(null);
  const [trend, setTrend] = useState([]);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);

  const api = `/schedule-analysis/${encodeURIComponent(jobNumber || '')}`;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2800);
  };

  const loadEvents = useCallback(async () => {
    if (!jobNumber) return;
    const res = await authenticatedApiCall(instance, accounts, 'GET', `${api}/events`);
    setEvents(res.data?.data?.events || []);
  }, [instance, accounts, jobNumber, api]);

  const loadRevs = useCallback(async () => {
    if (!jobNumber) return;
    const res = await authenticatedApiCall(instance, accounts, 'GET', `${api}/revisions`);
    const catalog = res.data?.data || {};
    const revs = catalog.revisions || [];
    setRevisions(revs);
    setRev((prev) => prev || String(catalog.selectedRev?.projId || revs[0]?.projId || ''));
  }, [instance, accounts, jobNumber, api]);

  const loadChat = useCallback(async () => {
    if (!jobNumber) return;
    const res = await authenticatedApiCall(instance, accounts, 'GET', `${api}/chat`);
    setChatMessages(res.data?.data?.messages || []);
  }, [instance, accounts, jobNumber, api]);

  const loadTrend = useCallback(async () => {
    if (!jobNumber) return;
    const res = await authenticatedApiCall(instance, accounts, 'GET', `${api}/dcma/trend`);
    setTrend(res.data?.data?.points || []);
  }, [instance, accounts, jobNumber, api]);

  const loadStoredRuns = useCallback(async () => {
    if (!jobNumber) return;
    const [w, d] = await Promise.all([
      authenticatedApiCall(instance, accounts, 'GET', `${api}/runs/windows`),
      authenticatedApiCall(instance, accounts, 'GET', `${api}/runs/dcma`),
    ]);
    if (w.data?.data?.payload) setWindows(w.data.data.payload);
    if (d.data?.data?.payload) setDcma(d.data.data.payload);
    const stamps = [w.data?.data?.created_at, d.data?.data?.created_at].filter(Boolean).sort();
    if (stamps.length) setLastAnalyzedAt(stamps[stamps.length - 1]);
  }, [instance, accounts, jobNumber, api]);

  useEffect(() => {
    if (!jobNumber || !allowed) return;
    setError(null);
    setBootDone(false);
    autoRanRef.current = false;
    networkFetchedRef.current = false;
    Promise.all([loadRevs(), loadEvents(), loadChat(), loadTrend(), loadStoredRuns()])
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setBootDone(true));
  }, [allowed, jobNumber, loadRevs, loadEvents, loadChat, loadTrend, loadStoredRuns]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  /** One click: events → windows → DCMA, with live stage updates. */
  const runFull = useCallback(async () => {
    if (!jobNumber) return;
    setBusy('full');
    setError(null);
    try {
      setRunStage('events');
      const ev = await authenticatedApiCall(instance, accounts, 'POST', `${api}/events/refresh`, {});
      setEvents(ev.data?.data?.events || []);
      setEventCounts(ev.data?.data?.counts || null);

      setRunStage('windows');
      const w = await authenticatedApiCall(instance, accounts, 'POST', `${api}/windows`, {});
      setWindows(w.data?.data || null);

      setRunStage('dcma');
      const d = await authenticatedApiCall(instance, accounts, 'POST', `${api}/dcma`, { rev: rev || null });
      setDcma(d.data?.data?.report || null);
      await loadTrend();

      setRunStage('done');
      setLastAnalyzedAt(new Date().toISOString());
      showToast('Full analysis complete');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
      setTimeout(() => setRunStage(null), 1500);
    }
  }, [instance, accounts, jobNumber, api, rev, loadTrend]);

  // First visit to a job with nothing stored: run the pipeline without being asked.
  useEffect(() => {
    if (!bootDone || autoRanRef.current || busy) return;
    if (!revisions.length) return;
    if (events.length || windows || dcma) return;
    autoRanRef.current = true;
    runFull();
  }, [bootDone, revisions, events, windows, dcma, busy, runFull]);

  const loadNetwork = useCallback(async () => {
    setBusy('network');
    setError(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${api}/model?rev=${encodeURIComponent(rev || '')}`);
      setNetwork(res.data?.data?.network || null);
      setSummary(res.data?.data?.summary || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  }, [instance, accounts, api, rev]);

  // Network tab loads itself the first time it is opened.
  useEffect(() => {
    if (tab !== 'network' || networkFetchedRef.current || !jobNumber || !allowed) return;
    networkFetchedRef.current = true;
    loadNetwork();
  }, [tab, jobNumber, allowed, loadNetwork]);

  const refreshEvents = async () => {
    setBusy('events');
    setError(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${api}/events/refresh`, {});
      const data = res.data?.data || {};
      setEvents(data.events || []);
      setEventCounts(data.counts || null);
      showToast(`Stored ${data.counts?.stored || 0} delay events`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  const sendChat = async (preset) => {
    const text = (preset || chatInput).trim();
    if (!text || chatBusy) return;
    setChatBusy(true);
    setChatInput('');
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${api}/chat`, { message: text });
      setChatMessages((prev) => [
        ...prev,
        { role: 'user', content: text },
        res.data?.data?.message || { role: 'assistant', content: res.data?.data?.reply },
      ]);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setChatBusy(false);
    }
  };

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `${api}/export/${kind}${kind === 'pdf' ? `?jobName=${encodeURIComponent(jobName || '')}` : ''}`,
        null,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data]);
      triggerDownload(blob, filenameFromHeader(res.headers, `${jobNumber}-schedule-analysis.${kind === 'xlsx' ? 'xlsx' : 'pdf'}`));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  const classCounts = useMemo(() => {
    const counts = {};
    for (const e of events) {
      const c = e.classification || 'other';
      counts[c] = (counts[c] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [events]);

  const filteredEvents = useMemo(() => {
    const q = eventSearch.trim().toLowerCase();
    return (events || []).filter((e) => {
      if (eventFilter !== 'all' && e.source !== eventFilter && e.classification !== eventFilter) return false;
      if (!q) return true;
      return `${e.task_code || ''} ${e.task_name || ''} ${e.text || ''} ${e.resp_party || ''}`.toLowerCase().includes(q);
    });
  }, [events, eventFilter, eventSearch]);

  const trendChart = useMemo(() => (
    (trend || [])
      .map((p) => ({
        x: Date.parse(p.data_date ? `${String(p.data_date).slice(0, 10)}T00:00:00` : p.created_at),
        score: Number(p.score),
      }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.score))
  ), [trend]);

  if (!allowed) {
    return (
      <div style={{ padding: 32, color: '#64748b' }}>
        Schedule Analysis is limited to Yousef.
      </div>
    );
  }

  if (!jobNumber) {
    return <div style={{ padding: 24 }}>Select a job to open Schedule Analysis.</div>;
  }

  const winList = [...(windows?.windows || [])].reverse(); // newest first for reading

  return (
    <div style={{ padding: '1rem 1.25rem 2rem', maxWidth: 1280, margin: '0 auto', background: '#f8fafc' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem', color: '#0f172a' }}>Schedule Analysis</h2>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {jobNumber}{jobName ? ` · ${jobName}` : ''} · delay evidence from P6 notebooks, weekly windows, and DCMA-14
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} />
          <label style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
            Rev
            <select value={rev} onChange={(e) => setRev(e.target.value)} style={{ ...btn, padding: '6px 8px' }}>
              {revisions.map((r) => (
                <option key={r.projId} value={r.projId}>{r.label || r.projShortName}</option>
              ))}
            </select>
          </label>
          <button type="button" style={btn} onClick={() => download('xlsx')} disabled={!!busy}>Excel</button>
          <button type="button" style={btn} onClick={() => download('pdf')} disabled={!!busy}>PDF</button>
          <ListenButton compact guideId="schedule-analysis-help" title="Schedule Analysis Help" accent={ACCENT} />
          <button type="button" style={btn} onClick={() => setShowHelp(true)}>Help</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #e2e8f0', marginBottom: 14 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              ...btn,
              border: 'none',
              borderRadius: 0,
              borderBottom: tab === t.id ? `2px solid ${ACCENT}` : '2px solid transparent',
              fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? ACCENT : '#64748b',
              background: 'transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#991b1b', padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {tab === 'overview' && (
        <ScheduleAnalysisOverview
          events={events}
          windows={windows}
          dcma={dcma}
          trend={trend}
          running={busy === 'full'}
          runStage={runStage}
          onRunFull={runFull}
          lastAnalyzedAt={lastAnalyzedAt}
          goTab={setTab}
        />
      )}

      {tab === 'events' && (
        <section>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <button type="button" style={btnPrimary} onClick={refreshEvents} disabled={busy === 'events' || busy === 'full'}>
              {busy === 'events' ? 'Reading P6…' : 'Refresh from notebooks'}
            </button>
            <input value={eventSearch} onChange={(e) => setEventSearch(e.target.value)} placeholder="Search activity or note" style={{ ...btn, minWidth: 220 }} />
            <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} style={btn}>
              <option value="all">All sources</option>
              <option value="notebook">Notebook</option>
              <option value="rfi">RFI</option>
              <option value="ncc">NCC</option>
              <option value="resp_party">Resp. Party</option>
              <option value="additional_scope">Additional Scope</option>
            </select>
            {eventCounts && (
              <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>
                {eventCounts.notebook} notebook · {eventCounts.rfi} RFI · {eventCounts.ncc} NCC · {eventCounts.resp_party} third party
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setEventFilter('all')}
              style={{ ...btn, padding: '3px 10px', fontSize: 12, borderRadius: 999, fontWeight: eventFilter === 'all' ? 700 : 500 }}
            >
              All {events.length}
            </button>
            {classCounts.map(([cls, n]) => (
              <button
                key={cls}
                type="button"
                onClick={() => setEventFilter(eventFilter === cls ? 'all' : cls)}
                style={{
                  ...btn,
                  padding: '3px 10px',
                  fontSize: 12,
                  borderRadius: 999,
                  background: eventFilter === cls ? (CLASS_COLOR[cls] || '#475569') : '#fff',
                  color: eventFilter === cls ? '#fff' : (CLASS_COLOR[cls] || '#475569'),
                  borderColor: CLASS_COLOR[cls] || '#cbd5e1',
                  fontWeight: 600,
                }}
              >
                {CLASS_LABEL[cls] || cls} {n}
              </button>
            ))}
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  {['Date', 'Activity', 'Source', 'Class', 'Resp. Party', 'Text'].map((h) => (
                    <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredEvents.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 18, color: '#64748b' }}>No events yet. Refresh from P6 notebooks (best on 75998).</td></tr>
                )}
                {filteredEvents.map((e) => (
                  <tr key={e.id || e.fingerprint} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{e.date_key || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{e.task_code || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{e.source}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{
                        background: CLASS_COLOR[e.classification] || '#475569',
                        color: '#fff',
                        borderRadius: 999,
                        padding: '1px 8px',
                        fontSize: 11,
                      }}>{e.classification || 'other'}</span>
                    </td>
                    <td style={{ padding: '8px 10px' }}>{e.resp_party || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{e.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'network' && (
        <section>
          <button type="button" style={btnPrimary} onClick={loadNetwork} disabled={busy === 'network'}>
            {busy === 'network' ? 'Loading…' : 'Reload network from P6'}
          </button>
          {summary && (
            <p style={{ color: '#475569', fontSize: 13 }}>
              {summary.projectShortName} · data date {summary.dataDate || '—'} · {summary.activityCount} activities · {summary.predecessorCount} relationships
            </p>
          )}
          {network && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, margin: '12px 0' }}>
              {[
                ['Open ends', network.openEndCount],
                ['Missing pred', network.missingPredecessorCount],
                ['Leads (neg lag)', network.leadCount],
                ['Lags', network.lagCount],
                ['FS / SS / FF / SF', `${network.predecessorTypes?.FS || 0} / ${network.predecessorTypes?.SS || 0} / ${network.predecessorTypes?.FF || 0} / ${network.predecessorTypes?.SF || 0}`],
                ['Driving path', network.drivingPathCount],
              ].map(([k, v]) => (
                <div key={k} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{k}</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{v}</div>
                </div>
              ))}
            </div>
          )}
          {network?.openEnds?.length > 0 && (
            <div>
              <h4 style={{ margin: '8px 0' }}>Open ends</h4>
              <ul style={{ fontSize: 13, columns: 2 }}>
                {network.openEnds.map((o) => (
                  <li key={o.taskCode}>{o.taskCode} {o.taskName} {o.missingPredecessor ? '(no pred)' : ''} {o.missingSuccessor ? '(no succ)' : ''}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {tab === 'windows' && (
        <section>
          <button type="button" style={btnPrimary} onClick={runFull} disabled={!!busy}>
            {busy === 'full' ? 'Analyzing…' : 'Re-run analysis'}
          </button>
          {winList.length === 0 && (
            <p style={{ color: '#64748b', fontSize: 13 }}>No windows yet — needs at least two dated P6 revisions.</p>
          )}
          {winList.map((w, i) => {
            const party = windowParty(w);
            const slip = w.projectFinishSlipDays ?? w.pathSlipDays ?? 0;
            return (
              <div key={i} style={{ marginTop: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 18, fontWeight: 800, minWidth: 64, textAlign: 'center',
                    color: slip > 0 ? '#b91c1c' : slip < 0 ? '#15803d' : '#475569',
                  }}>
                    {slip > 0 ? '+' : ''}{slip} d
                  </span>
                  <span style={{ fontWeight: 700 }}>
                    {w.from?.projShortName} → {w.to?.projShortName}
                  </span>
                  <span style={{
                    fontSize: 11, padding: '2px 10px', borderRadius: 999, color: '#fff',
                    background: PARTY_STYLE[party].color,
                  }}>
                    {PARTY_STYLE[party].label}
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {w.slipCount} slipping · joined path {w.drivingJoined} · left {w.drivingLeft} · {w.eventsOnPath?.length || 0} events on path
                    {w.to?.projectFinish ? ` · finish ${fmtIso(w.to.projectFinish)}` : ''}
                  </span>
                </div>
                <div style={{ padding: 12 }}>
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#64748b' }}>
                        <th style={{ paddingBottom: 4 }}>Activity</th><th>Finish slip</th><th>Driving</th><th>Party</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(w.pathSlips?.length ? w.pathSlips : w.topSlips || []).slice(0, 12).map((s) => (
                        <tr key={s.taskCode} style={{ borderTop: '1px solid #f8fafc' }}>
                          <td style={{ padding: '4px 4px' }}>{s.taskCode} {s.taskName}</td>
                          <td style={{ color: (s.finishSlipDays || 0) > 0 ? '#b91c1c' : '#15803d' }}>{s.finishSlipDays > 0 ? '+' : ''}{s.finishSlipDays}</td>
                          <td>{s.onDrivingPath ? '●' : ''}</td>
                          <td>{s.respParty || s.partyGroup}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {w.eventsOnPath?.length > 0 && (
                    <div style={{ marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Evidence on the driving path</div>
                      <ul style={{ fontSize: 12, margin: 0, paddingLeft: 16 }}>
                        {w.eventsOnPath.slice(0, 8).map((e, idx) => (
                          <li key={idx}>{e.date_key} <b>{e.task_code}</b> ({e.source}{e.resp_party ? ` · ${e.resp_party}` : ''}) {e.text}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {tab === 'health' && (
        <section>
          <button type="button" style={btnPrimary} onClick={runFull} disabled={!!busy}>
            {busy === 'full' ? 'Analyzing…' : 'Re-run analysis'}
          </button>
          {dcma && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 12 }}>
                <ScoreRing score={dcma.score} size={104} stroke={11} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{dcma.passCount} of {dcma.metricCount} checks pass</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>data date {dcma.dataDate || '—'} · {dcma.activityCount} activities · {dcma.predecessorCount} relationships</div>
                </div>
                {trendChart.length >= 2 && (
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <ResponsiveContainer width="100%" height={110}>
                      <LineChart data={trendChart} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="x"
                          type="number"
                          domain={['dataMin', 'dataMax']}
                          tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          tick={{ fontSize: 10 }}
                        />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={28} />
                        <Tooltip
                          labelFormatter={(t) => new Date(t).toLocaleDateString()}
                          formatter={(v) => [v, 'Score']}
                        />
                        <Line type="monotone" dataKey="score" stroke={ACCENT} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8 }}>
                {(dcma.metrics || []).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setDcmaOpen(dcmaOpen === m.id ? null : m.id)}
                    style={{
                      textAlign: 'left',
                      border: '1px solid #e2e8f0',
                      borderLeft: `4px solid ${m.value == null ? '#94a3b8' : m.pass ? '#15803d' : '#b91c1c'}`,
                      borderRadius: 8,
                      padding: 10,
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b' }}>
                      <span>{m.id}. {m.value == null ? 'n/a' : m.pass ? 'Pass' : 'Fail'}</span>
                      <span style={{ fontWeight: 700, color: m.value == null ? '#94a3b8' : m.pass ? '#15803d' : '#b91c1c' }}>{fmtMetricValue(m)}</span>
                    </div>
                    <div style={{ fontWeight: 650, fontSize: 13 }}>{m.name}</div>
                    <div style={{ fontSize: 12, color: '#475569' }}>{m.count} of {m.total}</div>
                    {dcmaOpen === m.id && (m.items || []).slice(0, 12).map((it) => (
                      <div key={it.taskCode || it.predecessorCode} style={{ fontSize: 11, color: '#334155', marginTop: 2 }}>
                        {it.taskCode || `${it.predecessorCode} → ${it.successorCode}`} {it.taskName || it.type || ''}
                      </div>
                    ))}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {tab === 'standard' && (
        <DataStandardTab jobNumber={jobNumber} instance={instance} accounts={accounts} api={api} btn={btn} btnPrimary={btnPrimary} />
      )}

      {tab === 'chat' && (
        <section style={{ display: 'flex', flexDirection: 'column', minHeight: 420, background: '#0f172a', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', background: ACCENT, color: '#fff', fontWeight: 700 }}>Forensic chat</div>
          <div style={{ flex: 1, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {chatMessages.length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>
                Ask why a date moved. Answers cite notebook dates and activity IDs only — nothing is invented.
              </div>
            )}
            {chatMessages.map((m, i) => (
              <div key={m.id || i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                background: m.role === 'user' ? ACCENT : '#1e293b',
                color: '#fff',
                padding: '8px 10px',
                borderRadius: 10,
                maxWidth: '80%',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
              }}>
                {m.content}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          {chatMessages.length === 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 10px 8px' }}>
              {CHAT_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => sendChat(s)}
                  disabled={chatBusy}
                  style={{ ...btn, background: '#1e293b', color: '#cbd5e1', borderColor: '#334155', fontSize: 12, borderRadius: 999 }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <form
            onSubmit={(e) => { e.preventDefault(); sendChat(); }}
            style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid #334155' }}
          >
            <input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Why did conveyor slip last week?"
              style={{ flex: 1, ...btn, background: '#1e293b', color: '#fff', borderColor: '#334155' }}
            />
            <button type="submit" style={btnPrimary} disabled={chatBusy}>{chatBusy ? '…' : 'Send'}</button>
          </form>
        </section>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, background: toast.type === 'success' ? ACCENT : '#b91c1c',
          color: '#fff', padding: '10px 14px', borderRadius: 8, zIndex: 50,
        }}>{toast.message}</div>
      )}

      {showHelp && (
        <div onClick={() => setShowHelp(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 820, maxWidth: '94vw', maxHeight: '88vh', borderRadius: 12, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Schedule Analysis</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <ListenButton compact guideId="schedule-analysis-help" title="Schedule Analysis Help" accent={ACCENT} dark />
                <button type="button" onClick={() => setShowHelp(false)} style={{ ...btn, border: 'none' }}>×</button>
              </div>
            </div>
            <div style={{ padding: 16, overflow: 'auto' }}>
              <HelpWithVideoTour guideId="schedule-analysis-help" title="Schedule Analysis" accent={ACCENT}>
                <p>The Overview runs the whole pipeline in one click: it reads dated P6 notebook lines into a delay register, compares every weekly revision for slip and driving-path changes, and scores DCMA-14. The brief and charts summarize what moved and who owns it.</p>
                <p>Detail tabs hold the evidence: Events is the register, Windows shows each week-to-week comparison with the notebook lines that sat on the driving path, Health is the DCMA scorecard, and Chat only answers from those stored facts.</p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleAnalysis;
